'use client'

import type { ReactNode } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA, NOT_SET } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'

import { VENUE_LOCATION_MODULE_NAME } from './constants'

interface TrackedVenueLinkProps {
  readonly href: string
  /** Texto visible del enlace, sin normalizar — se slugifica antes del push. */
  readonly linkText: string
  /**
   * `content_link` → nombre del recinto (`Ubicación Detalles Producto.csv` →
   * "Click en (Localización)/Más eventos en este recinto"). `outbound` →
   * "Cómo llegar" (misma ficha, fila "Click en Cómo llegar"): abre en pestaña
   * nueva y añade `click_subtype='maps'` + `link_domain` real del destino.
   */
  readonly kind: 'content_link' | 'outbound'
  readonly className?: string
  readonly children: ReactNode
}

/** Hostname real de `href`, o `NOT_SET` si no es una URL absoluta parseable. */
function resolveLinkDomain(href: string): string {
  try {
    return new URL(href).hostname.toLowerCase()
  } catch {
    return NOT_SET
  }
}

/**
 * `TrackedVenueLink` — enlace con tracking de `click` para la sección de
 * recinto de la PDP (`PerformanceVenue`, `Ubicación Detalles Producto.csv`).
 *
 * Client Component (hoja): `PerformanceVenue` es Server Component y no puede
 * definir el `onClick` que necesita `useOptionalPageAnalytics` — mismo patrón
 * que `AlphaEntryLink`/`Breadcrumb`. Sin `preventDefault`: el push es síncrono
 * y la navegación (interna o `target="_blank"`) sigue su curso normal.
 */
export function TrackedVenueLink({ href, linkText, kind, className, children }: TrackedVenueLinkProps) {
  const analytics = useOptionalPageAnalytics()
  const isOutbound = kind === 'outbound'

  function handleClick() {
    analytics?.trackClick({
      click_type: kind,
      click_subtype: isOutbound ? 'maps' : NO_APLICA,
      link_text: slugify(linkText),
      link_url: href,
      link_domain: isOutbound ? resolveLinkDomain(href) : NO_APLICA,
      module_name: VENUE_LOCATION_MODULE_NAME,
      module_type: NO_APLICA,
      nav_level: NO_APLICA,
      nav_path: NO_APLICA,
      action_detail: NO_APLICA,
      action_value: NO_APLICA,
      action_context: NO_APLICA,
    })
  }

  if (isOutbound) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={handleClick}
      >
        {children}
      </a>
    )
  }

  return (
    <a href={href} className={className} onClick={handleClick}>
      {children}
    </a>
  )
}
